import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'

export interface Message {
  id: string
  content: string
  type: 'TEXT' | 'PHOTO' | 'VOICE'
  senderId: string
  sender: {
    id: string
    profile: {
      name: string
    }
  }
  createdAt: string
  readAt?: string
}

export interface MessagesResponse {
  messages: Message[]
  total: number
  limit: number
  offset: number
}

export function useMessages(matchId: string, limit: number = 50, offset: number = 0) {
  return useQuery({
    queryKey: ['messages', matchId, limit, offset],
    queryFn: async () => {
      const response = await apiClient.get<MessagesResponse>(
        `/api/matches/${matchId}/messages?limit=${limit}&offset=${offset}`,
      )
      return response
    },
    enabled: !!matchId,
    staleTime: 10 * 1000, // 10 seconds
  })
}

export function useSendMessage(matchId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { type: 'TEXT' | 'PHOTO' | 'VOICE'; content: string }) => {
      const response = await apiClient.post(`/api/matches/${matchId}/messages`, data)
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', matchId] })
    },
  })
}

export function useMarkMessagesAsRead(matchId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (messageIds: string[]) => {
      const response = await apiClient.post(
        `/api/matches/${matchId}/messages/mark-read`,
        { messageIds },
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', matchId] })
    },
  })
}

export function useDeleteMessage(matchId: string, messageId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.delete(
        `/api/matches/${matchId}/messages/${messageId}`,
      )
      return response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages', matchId] })
    },
  })
}
